// Ported from packages/workflow-engine-upstream/ui/src/PlanCard.tsx at commit 48aafac; only imports changed.
// The plan card (design §5): drawn by the engine's code from the checked plan and sent with the
// plan_proposed event. It draws whatever the card holds: why this version exists, the reviewer's
// flags, each part of the question with the steps that answer it, each step with code's facts
// beside it, and every value with a badge for where it came from. A reading is marked to check,
// with why, and for a pick the others it could have been.
import type { RunEvent } from './contracts';
import type { CardMatch, CardStep, CardValue, PlanCard as Card } from './contracts/card-types';

const STATUS_WORDS: Record<string, string> = {
  answered: 'answered by the steps below',
  asked: 'asked of you',
  cant: "can't be answered",
  filler: 'no meaning, left out',
  purpose: 'your reason, the answer speaks to it',
};

const ORIGIN: Record<CardValue['source'], { label: string; cls: string }> = {
  yours: { label: 'your words', cls: 'chip ok' },
  answer: { label: 'your answer', cls: 'chip ok' },
  data: { label: 'from the data', cls: 'chip def' },
  document: { label: 'from a document', cls: 'chip def' },
  reading: { label: 'my reading, please check', cls: 'chip warn' },
};

/** The card the engine sent with this plan version, if any. */
export function planCardOf(events: RunEvent[], version: number): Card | null {
  const e = [...events].reverse().find((x) => x.type === 'plan_proposed' && x.planVersion === version);
  const card = e?.payload?.card;
  const c = card as Partial<Card> | undefined;
  return c && typeof c === 'object' && Array.isArray(c.parts) && Array.isArray(c.steps) && Array.isArray(c.values) && Array.isArray(c.flags) ? (c as Card) : null;
}

const counted = (xs: { value: string; count: number }[]) => xs.map((x) => `"${x.value}" (${x.count})`).join(', ') || 'none';

function Matches({ matches }: { matches: CardMatch[] }) {
  return (
    <>
      {matches.map((m) => (
        <div key={`${m.field}|${m.value}`} className="part-line">
          {m.field} holding "{m.value}": matched {counted(m.matched)}
          <span className="note"> · left out {counted(m.left)}</span>
        </div>
      ))}
    </>
  );
}

function Step({ s }: { s: CardStep }) {
  return (
    <div className={s.warning ? 'reading-row check' : 'reading-row'}>
      <b>{s.id}</b> <span className="note">{s.kind}</span> {s.text}
      {s.method && (
        <div className="part-line">
          Method step {s.method.document === 'question' ? `${s.method.ref} of your list` : `${s.method.document} #${s.method.ref}`}: "{s.method.quote}"
        </div>
      )}
      {s.facts.map((f, i) => (
        <div key={`${f.label}|${i}`} className="part-line">
          <span className="note">{f.label}:</span> {f.label === 'query' ? <span className="mono">{f.text}</span> : f.text}
        </div>
      ))}
      {s.matches && <Matches matches={s.matches} />}
      {s.warning && <div className="note">{s.warning}</div>}
    </div>
  );
}

function Value({ v }: { v: CardValue }) {
  const origin = ORIGIN[v.source];
  return (
    <div className={v.source === 'reading' ? 'reading-row check' : 'reading-row'}>
      <span className="mono">{v.name}</span> = <b>{v.shown}</b> <span className={origin.cls}>{origin.label}</span>
      <div className="part-line">{v.detail}</div>
      {v.others && <div className="part-line">It could also have been: {v.others.join('; ')}</div>}
      {v.also && <div className="part-line">Other sources give: {v.also.join('; ')}</div>}
      {v.matches && <Matches matches={v.matches} />}
      {v.steps.length > 0 && <span className="note">Used by {v.steps.join(', ')}</span>}
    </div>
  );
}

export function PlanCard({ card }: { card: Card }) {
  const steps = new Map(card.steps.map((s) => [s.id, s] as const));
  return (
    <div className="reading-card">
      <span className="h">How I read your question</span>
      {card.why && (
        <span className="reading-row check">
          <b>Why this version:</b> {card.why}
        </span>
      )}
      {card.flags.map((f) => (
        <span key={`${f.at}|${f.problem}`} className="reading-row check">
          <b>Reviewer{f.severity === 'block' ? ', to settle' : ''} ({f.at}):</b> {f.problem}
        </span>
      ))}
      {card.parts.map((p) => (
        <div key={p.id} className={p.status === 'cant' || p.status === 'asked' ? 'reading-row check' : 'reading-row'}>
          <b>"{p.quote}"</b>{' '}
          <span className="note">
            · {STATUS_WORDS[p.status] ?? p.status}
            {p.note ? `: ${p.note}` : ''}
          </span>
          {p.steps.map((id) => (
            <div key={id} className="part-line">
              {id}: {steps.get(id)?.text ?? ''}
            </div>
          ))}
        </div>
      ))}
      {card.expectation && (
        <span className="reading-row">
          <b>What your question presumes:</b> "{card.expectation.quote}", checked by {card.expectation.step} ({card.expectation.claim})
        </span>
      )}
      <span className="h">Values</span>
      {card.values.map((v) => (
        <Value key={v.name} v={v} />
      ))}
      {card.subjects?.length ? <span className="h">What each tag is, from the data</span> : null}
      {card.subjects?.map((n) => (
        <span key={n.id} className={n.quote === null ? 'reading-row check' : 'reading-row'}>
          <b className="mono">{n.id}</b>{' '}
          {n.quote !== null ? <>"{n.quote}" <span className="note">· {n.from}</span></> : <span className="note">· no result says what it is</span>}
        </span>
      ))}
      <span className="h">Steps</span>
      {card.steps.map((s) => (
        <Step key={s.id} s={s} />
      ))}
      {card.methodNotes?.map((n) => (
        <span key={n} className="reading-row check">
          <b>Method:</b> {n}
        </span>
      ))}
      <span className="note">Now is {card.now.slice(0, 16).replace('T', ' ')}.</span>
    </div>
  );
}
